import { decodeJWT } from 'aws-amplify/auth';
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito';

import amplifyConfig from '@/configs/amplify-config';

const SIT_HOST = 'www.sit1.gb.moneysavingexpert.com';

export interface CognitoInitiateAuthResponse {
  AuthenticationResult?: {
    AccessToken?: string
    IdToken?: string
    RefreshToken?: string
  }
  ChallengeName?: string
}

export interface ReauthenticationTokens {
  accessToken: string
  idToken: string
  refreshToken: string
}

function getProxyConfig() {
  if (import.meta.env.VITE_PROXY_ENV === 'SIT') {
    return {
      Auth: {
        Cognito: {
          ...amplifyConfig[SIT_HOST].Auth.Cognito,
          userPoolEndpoint: 'https://localhost:5173/idp',
        },
      },
    };
  } else {
    return {
      Auth: {
        Cognito: {
          ...amplifyConfig.default.Auth.Cognito,
          userPoolEndpoint: 'https://localhost:5173/idp',
        },
      },
    };
  };
}

export const getConfig = () => {
  const proxyConfig = getProxyConfig();

  return amplifyConfig[window.location.hostname]
    ? amplifyConfig[window.location.hostname]
    : import.meta.env.VITE_PROXY_ENV
      ? proxyConfig
      : amplifyConfig.default;
};

// A 200 InitiateAuth response is only a completed authentication when it carries tokens and no challenge.
export const getReauthenticationTokens = (response: CognitoInitiateAuthResponse): ReauthenticationTokens => {
  if (response.ChallengeName) {
    throw new Error(`InitiateAuth returned an unsupported challenge: ${response.ChallengeName}`);
  }

  const { AccessToken, IdToken, RefreshToken } = response.AuthenticationResult ?? {};

  if (!AccessToken || !IdToken || !RefreshToken) {
    throw new Error('InitiateAuth response did not contain a complete AuthenticationResult');
  }

  return { accessToken: AccessToken, idToken: IdToken, refreshToken: RefreshToken };
};

// Overwrites the signed-in user's cached Amplify tokens in place, using the same token store Amplify's signIn uses.
export const replaceAmplifyTokens = async ({ accessToken, idToken, refreshToken }: ReauthenticationTokens): Promise<void> => {
  const { tokenOrchestrator } = cognitoUserPoolsTokenProvider;
  const currentTokens = await tokenOrchestrator.getTokenStore().loadTokens();

  if (!currentTokens?.idToken) {
    throw new Error('No existing Amplify session to update');
  }

  const decodedAccessToken = decodeJWT(accessToken);
  const decodedIdToken = decodeJWT(idToken);
  const reauthenticatedUserId = decodedIdToken.payload.sub;

  if (!reauthenticatedUserId || reauthenticatedUserId !== currentTokens.idToken.payload.sub) {
    throw new Error('Reauthenticated user does not match the signed-in user');
  }

  const issuedAtInMillis = (decodedAccessToken.payload.iat ?? 0) * 1000;

  await tokenOrchestrator.setTokens({
    tokens: {
      accessToken: decodedAccessToken,
      idToken: decodedIdToken,
      refreshToken,
      clockDrift: issuedAtInMillis > 0 ? issuedAtInMillis - Date.now() : 0,
      username: currentTokens.username,
      signInDetails: currentTokens.signInDetails,
    },
  });
};